/**
 * Helps COD / checkout apps recover after AJAX cart morphs and on full page lifecycle.
 *
 * Listening to Theme `cart:update` catches every CartAddEvent + CartUpdateEvent (qty, discounts),
 * since morphSection alone may race late-binding vendor scripts.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { notifyThirdPartyCartScripts } from '@theme/section-renderer';

/** @returns {HTMLElement} */
function pageScope() {
  return /** @type {HTMLElement} */ (document.getElementById('header-group') ?? document.body);
}

function burst() {
  notifyThirdPartyCartScripts(pageScope());
}

// Extra retries for COD apps that finish async initialization > 600 ms after their script loads.
// 500 ms covers apps that initialize just after window.load.
// 1500 ms covers apps that make an API call during init.
// 3000 ms covers slow mobile connections or apps with multi-step async bootstrap.
function burstAndRetry() {
  burst();
  setTimeout(burst, 500);
  setTimeout(burst, 1500);
  setTimeout(burst, 3000);
}

// DOMContentLoaded: catches apps already present in the DOM before all resources finish.
document.addEventListener('DOMContentLoaded', burst);
// load: fires after every external script (including the COD app) has executed — main fix for
// the "button disappears on refresh" case where the app initializes after DOMContentLoaded.
window.addEventListener('load', burstAndRetry);
// pageshow: covers bfcache restores and View Transition navigations (load does not fire then).
window.addEventListener('pageshow', burstAndRetry);
document.addEventListener(ThemeEvents.cartUpdate, burst);

/**
 * COD apps (e.g. Releasit) place orders through their own backend, bypassing Shopify
 * checkout entirely. They empty the cart themselves by calling the native
 * `/cart/clear.js` endpoint directly, which the theme has no other way of knowing about.
 * Watch for that call (XHR or fetch, whichever the app uses) and sync the cart drawer +
 * cart icon the same way the theme does for its own cart mutations.
 */
const CART_CLEAR_URL_PATTERN = /\/cart\/clear\.js(\?|$)/;

/** @param {unknown} url */
function isCartClearUrl(url) {
  if (!url) return false;
  try {
    return CART_CLEAR_URL_PATTERN.test(String(url));
  } catch (_) {
    return false;
  }
}

function notifyCartCleared() {
  document.dispatchEvent(
    new CartUpdateEvent({}, 'cart-app-compat', { itemCount: 0, source: 'third-party-cart-clear' })
  );
}

const originalXhrOpen = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function (method, url, ...rest) {
  this.__isCartClearRequest = isCartClearUrl(url);
  return originalXhrOpen.call(this, method, url, ...rest);
};

const originalXhrSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.send = function (...args) {
  if (this.__isCartClearRequest) {
    this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) notifyCartCleared();
    });
  }
  return originalXhrSend.apply(this, args);
};

const originalFetch = window.fetch;
window.fetch = function (input, init) {
  const url = typeof input === 'string' || input instanceof URL ? input : input?.url;
  const isClearRequest = isCartClearUrl(url);
  const result = originalFetch.call(this, input, init);

  if (isClearRequest) {
    result.then((response) => response.ok && notifyCartCleared()).catch(() => {});
  }

  return result;
};
